import { useTranslation } from "react-i18next";
import {
  AwardCriterionType,
  AwardSubCriterionAnswerFormat,
  Currency,
  useGetProductGroupsAsSupplierQuery,
  type BidAwardCriterionResponseDto,
  type BidDto,
  type BidPatchDto,
  type PortalSourcingEventDetailDto,
} from "../../../../autogen/bff-api";
import { productBidTotal } from "../../../../common/products/productBidTotal";
import {
  answerRowsOf,
  groupScorableAwardRows,
  scorableAwardRows,
  type ScorableAwardRow,
} from "../../../../common/award-criteria/scorableAwardRows";
import { useEditor } from "../../../../common/editing/save-coordinator";
import { isAwardCriterionAnswerComplete } from "./AwardCriterionAnswerCard";
import { isBidEditable } from "./publicBidEditability";
import { criterionTypeLabelKey } from "../../buyer/wizard/utils/publicProcurementOptions";

/**
 * The tendered price is one figure the price model scores for every price row, so it is asked for once.
 * A competition awarded on the lowest price has no criteria at all, and one whose criteria happen to
 * carry no price row still needs the figure — both get a standalone price card. An event that does not
 * collect a price drops its price rows instead of asking for something it will not use.
 */
const SYNTHETIC_PRICE_ROW_ID = "price";

/**
 * One answer, with the fields the patch does not mention carried over from what is already stored.
 *
 * The bid is saved with a merge patch, and inside a list the merge walker replaces elements rather
 * than merging them — so a field left out of this object does not mean "unchanged", it reaches the
 * BFF as null and clears what the supplier wrote. Every field of the response has to be listed here,
 * which is exactly the kind of rule that is quietly broken by adding a field somewhere else.
 */
export const mergeAwardCriterionResponse = (
  current: BidAwardCriterionResponseDto | undefined,
  criterionId: string,
  patch: Partial<BidAwardCriterionResponseDto>,
): BidAwardCriterionResponseDto => ({
  criterionId,
  text: current?.text ?? null,
  number: current?.number ?? null,
  choice: current?.choice ?? null,
  partialElaboration: current?.partialElaboration ?? null,
  attachmentIds: current?.attachmentIds ?? [],
  ...patch,
});

const withPriceRow = (rows: ScorableAwardRow[], collectsPrice: boolean, priceLabel: string): ScorableAwardRow[] => {
  if (!collectsPrice) return rows.filter((row) => !row.isPrice);
  if (rows.some((row) => row.isPrice)) return rows;
  return [
    {
      id: SYNTHETIC_PRICE_ROW_ID,
      parentId: null,
      groupLabel: priceLabel,
      groupWeightPercent: null,
      type: AwardCriterionType.Price,
      name: priceLabel,
      description: "",
      ownWeightPercent: null,
      effectiveWeightPercent: null,
      answerFormat: AwardSubCriterionAnswerFormat.Number,
      requiresDocumentation: false,
      isPrice: true,
    },
    ...rows,
  ];
};

/**
 * The supplier's side of the award criteria. The rows come from the event, the answers from the bid,
 * and the price rows write the tendered price rather than a response of their own — the evaluation
 * scores every price row from that single figure.
 */
export const useAwardCriterionAnswers = (bidId: string, event: PortalSourcingEventDetailDto) => {
  const { t } = useTranslation();
  const { state, updateField, fieldStatus } = useEditor<BidDto, BidPatchDto>(bidId);
  const { data: productData, isSuccess: productsSettled } = useGetProductGroupsAsSupplierQuery({
    sourcingEventId: event.id,
  });
  const productGroups = productData?.productGroups ?? [];
  const priceComesFromProducts = productGroups.length > 0;
  const collectsPrice = event.collectSupplierPrice ?? true;
  const rows = withPriceRow(
    scorableAwardRows(event.awardCriteria?.criteria, (type: AwardCriterionType) => t(criterionTypeLabelKey(type))),
    collectsPrice || priceComesFromProducts,
    t("Price"),
  );
  const responses = state.awardCriterionResponses ?? [];
  const currency = event.estimatedValue?.currency ?? Currency.Nok;
  const price = priceComesFromProducts
    ? productBidTotal(productGroups, state.products)
    : state.price?.value ?? null;
  const readOnly = !isBidEditable(state);

  const responseFor = (row: ScorableAwardRow) => responses.find((response) => response.criterionId === row.id);

  const upsert = (row: ScorableAwardRow, patch: Partial<BidAwardCriterionResponseDto>) => {
    if (readOnly) return;
    // A price row's answer is the tendered price, and the synthetic one has no criterion id to key a
    // response by — sending one would be rejected server-side.
    if (row.isPrice) return;
    const next = mergeAwardCriterionResponse(responseFor(row), row.id, patch);
    updateField(
      { awardCriterionResponses: [...responses.filter((r) => r.criterionId !== row.id), next] },
      { debounceMs: 300 },
    );
  };

  const setPrice = (value: number | null) => {
    if (readOnly || !productsSettled || priceComesFromProducts) return;
    updateField({ price: { value, currency } }, { debounceMs: 400 });
  };

  const groups = groupScorableAwardRows(rows);
  const answerRows = groups.flatMap(answerRowsOf);

  return {
    rows,
    groups,
    answerRows,
    responseFor,
    upsert,
    price,
    priceComesFromProducts,
    priceEditingLocked: !productsSettled,
    currency,
    setPrice,
    answeredCount: answerRows.filter((row) => isAwardCriterionAnswerComplete(row, responseFor(row), price)).length,
    isSaving:
      fieldStatus("awardCriterionResponses") === "saving" ||
      fieldStatus("awardCriterionResponses") === "pending" ||
      fieldStatus("price") === "saving" ||
      fieldStatus("price") === "pending",
    readOnly,
  };
};
